import React, { useState, useEffect } from 'react';
import axios from '../../axiosConfig';
import { IoSearchOutline } from 'react-icons/io5';
import '../../styles/SurveyAssignmentModal.css';

const SurveyAssignmentModal = ({ isOpen, onClose, survey }) => {
    const [dogs, setDogs] = useState([]);
    const [filteredDogs, setFilteredDogs] = useState([]);
    const [selectedDogIds, setSelectedDogIds] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
            setError('');
            setSuccessMessage('');
        }
    }, [isOpen]);

    // Fetch data when modal opens
    useEffect(() => {
        if (isOpen && survey) {
            fetchData();
        }
    }, [isOpen, survey]);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            setError('');
            setSuccessMessage('');

            if (!survey || !survey.id || !survey.schoolId) {
                setError('Nieprawidłowe dane ankiety');
                setIsLoading(false);
                return;
            }

            const token = localStorage.getItem('jwt');

            // Fetch all dogs for the school
            const dogsResponse = await axios.get(`/api/v1/dog/list/${survey.schoolId}/booking`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Check which dogs already have the survey assigned
            const dogsWithStatus = await checkAssignments(dogsResponse.data, survey.id);

            setDogs(dogsWithStatus);
            setFilteredDogs(dogsWithStatus);

            // Pre-select already assigned dogs
            const preSelected = dogsWithStatus
                .filter(dog => dog.hasSurveyAssigned)
                .map(dog => String(dog.id));
            setSelectedDogIds(preSelected);

        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Nie udało się pobrać danych. Spróbuj ponownie później.');
        } finally {
            setIsLoading(false);
        }
    };

    const checkAssignments = async (dogsList, surveyId) => {
        const token = localStorage.getItem('jwt');

        const assignmentPromises = dogsList.map(async (dog) => {
            try {
                const response = await axios.get(`/api/v1/survey/dog/${dog.id}/assigned`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                return {
                    ...dog,
                    hasSurveyAssigned: response.data.some(s => s.id === surveyId)
                };
            } catch (err) {
                console.error(`Error checking assignments for dog ${dog.id}:`, err);
                return { ...dog, hasSurveyAssigned: false };
            }
        });

        return Promise.all(assignmentPromises);
    };

    // Search handler
    const handleSearch = (term) => {
        setSearchTerm(term);

        if (!term.trim()) {
            setFilteredDogs(dogs);
            return;
        }

        const lowerCaseTerm = term.toLowerCase();
        const filtered = dogs.filter(dog =>
            (dog.name && dog.name.toLowerCase().includes(lowerCaseTerm)) ||
            (dog.breed && dog.breed.toLowerCase().includes(lowerCaseTerm)) ||
            (dog.ownersName && dog.ownersName.toLowerCase().includes(lowerCaseTerm))
        );

        setFilteredDogs(filtered);
    };

    // Selection handlers
    const toggleDogSelection = (dogId) => {
        const dogIdStr = String(dogId);
        setSelectedDogIds(prev => {
            if (prev.includes(dogIdStr)) {
                return prev.filter(id => id !== dogIdStr);
            } else {
                return [...prev, dogIdStr];
            }
        });
    };

    const selectAll = () => {
        const allIds = filteredDogs.map(dog => String(dog.id));
        setSelectedDogIds(prev => {
            const newSet = new Set([...prev, ...allIds]);
            return Array.from(newSet);
        });
    };

    const deselectAll = () => {
        setSelectedDogIds([]);
    };

    // Submit handler
    const handleSubmit = async () => {
        setIsSubmitting(true);
        setSuccessMessage('');
        setError('');

        try {
            const token = localStorage.getItem('jwt');

            // Get currently assigned dog IDs
            const currentlyAssignedIds = dogs
                .filter(dog => dog.hasSurveyAssigned)
                .map(dog => String(dog.id));

            // Determine changes
            const toAssign = selectedDogIds.filter(id => !currentlyAssignedIds.includes(id));
            const toUnassign = currentlyAssignedIds.filter(id => !selectedDogIds.includes(id));

            // Create new assignments
            await Promise.all(toAssign.map(dogIdStr => {
                const dog = dogs.find(d => String(d.id) === dogIdStr);
                return axios.post('/api/v1/survey/assign', {
                    surveyId: survey.id,
                    dogId: dog.id
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }));

            // Remove old assignments
            await Promise.all(toUnassign.map(dogIdStr => {
                const dog = dogs.find(d => String(d.id) === dogIdStr);
                return axios.delete(`/api/v1/survey/unassign/dog/${dog.id}/survey/${survey.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }));

            const totalChanges = toAssign.length + toUnassign.length;

            if (totalChanges > 0) {
                setSuccessMessage(`Zapisano! Dodano: ${toAssign.length}, usunięto: ${toUnassign.length}`);
            } else {
                setSuccessMessage('Brak zmian do zapisania.');
            }

            // Refresh data
            await fetchData();

            // Close after short delay
            setTimeout(() => {
                onClose();
            }, 1500);

        } catch (err) {
            console.error('Error updating assignments:', err);
            setError('Wystąpił błąd podczas zapisywania. Spróbuj ponownie.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle click outside modal
    const handleOverlayClick = (e) => {
        if (e.target.classList.contains('sam-overlay')) {
            onClose();
        }
    };

    if (!isOpen) return null;

    // Calculate if there are changes
    const currentlyAssignedIds = dogs
        .filter(dog => dog.hasSurveyAssigned)
        .map(dog => String(dog.id));
    const hasChanges =
        selectedDogIds.some(id => !currentlyAssignedIds.includes(id)) ||
        currentlyAssignedIds.some(id => !selectedDogIds.includes(id));

    return (
        <div className="sam-overlay" onClick={handleOverlayClick}>
            <div className="sam-modal">
                {/* Header */}
                <div className="sam-header">
                    <h2 className="sam-title">Przypisz</h2>
                </div>

                <div className="sam-content">
                    {/* Messages */}
                    {successMessage && (
                        <div className="sam-success-message">
                            <p>{successMessage}</p>
                        </div>
                    )}

                    {error && (
                        <div className="sam-error-message">
                            <p>{error}</p>
                        </div>
                    )}

                    {isLoading ? (
                        <div className="sam-loading">
                            <p>Ładowanie danych...</p>
                        </div>
                    ) : (
                        <>
                            {/* Search Bar */}
                            <div className="sam-search-bar">
                                <div className="sam-search-input-wrapper">
                                    <IoSearchOutline className="sam-search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Szukaj"
                                        value={searchTerm}
                                        onChange={(e) => handleSearch(e.target.value)}
                                        className="sam-search-input"
                                    />
                                    {searchTerm && (
                                        <button
                                            className="sam-search-clear-btn"
                                            onClick={() => handleSearch('')}
                                            type="button"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Selection Controls */}
                            <div className="sam-selection-controls">
                                <span className="sam-selection-info">
                                    Wybrano {selectedDogIds.length} z {filteredDogs.length} psów
                                </span>
                                <div className="sam-selection-buttons">
                                    <button onClick={selectAll} className="sam-select-all-btn">
                                        Zaznacz wszystkie
                                    </button>
                                    <button onClick={deselectAll} className="sam-deselect-all-btn">
                                        Odznacz wszystkie
                                    </button>
                                </div>
                            </div>

                            {/* Dogs List */}
                            {filteredDogs.length === 0 ? (
                                <div className="sam-no-results">
                                    <p>Nie znaleziono psów pasujących do wyszukiwania.</p>
                                </div>
                            ) : (
                                <div className="sam-dogs-list">
                                    {filteredDogs.map(dog => {
                                        const isSelected = selectedDogIds.includes(String(dog.id));

                                        return (
                                            <div
                                                key={dog.id}
                                                className={`sam-dog-item ${isSelected ? 'sam-dog-item--selected' : ''}`}
                                                onClick={() => toggleDogSelection(dog.id)}
                                            >
                                                <div className="sam-dog-main">
                                                    <div className="sam-dog-info">
                                                        <h3 className="sam-dog-name">{dog.name}</h3>
                                                        <p className="sam-dog-breed">{dog.breed || 'Brak rasy'}</p>
                                                    </div>
                                                    {dog.hasSurveyAssigned && (
                                                        <div className="sam-dog-badge">
                                                            Przypisane
                                                        </div>
                                                    )}
                                                </div>

                                                {(dog.ownersName || dog.ownerName) && (
                                                    <div className="sam-dog-owner">
                                                        <span className="sam-owner-label">Właściciel</span>
                                                        <span className="sam-owner-value">{dog.ownersName || dog.ownerName}</span>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="sam-footer">
                    <button onClick={onClose} className="sam-btn-cancel">
                        Anuluj
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="sam-btn-submit"
                        disabled={isSubmitting || !hasChanges}
                    >
                        {isSubmitting ? 'Zapisywanie...' : 'Przypisz'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SurveyAssignmentModal;